import React, { useState, useRef, useEffect } from 'react';
import Header from '../authentication/Header';
import Footer from '../authentication/Footer';
import './HeirloomAISearchPage.css';

const AI_MODELS = [
    { platform: 'Claude',     models: ['Opus 4.8', 'Opus 4.7'] },
    { platform: 'TwelveLabs', models: ['Marengo3.0', 'Pegasus1.5'] },
];

const EXCLUDE = new Set(['Home', 'Channels', 'AI Search', 'Search']);

function getCategories() {
    const storage  = window.app && window.app.storage;
    const appName  = (storage && storage.getItem('appName')) || '';
    const urlApp   = (window.app && window.app.urlAppName) || '';

    let all = [];

    if (urlApp === 'heirloom') {
        all = ['Family', 'Siblings', 'Friends'];
    } else if (['adidasboxing','morehousecollege','ganekimmersivestudios','uclatheatre','5120'].some(k => appName.includes(k))) {
        all = ['Movies', 'TV Shows', 'Podcasts', 'Music Videos', 'Interviews'];
    } else if (['1111','tv6ghana','kyliejenner'].some(k => appName.includes(k))) {
        all = ['Movies', 'TV Shows', 'Music Videos', 'Interviews'];
    } else if (appName.includes('kardashiancareers')) {
        all = ['Movies', 'TV Shows', 'Music Videos', 'Interviews', 'Commercials'];
    } else if (appName.includes('paff')) {
        all = ['Movies', 'TV Shows'];
    } else if (appName.includes('screenixx')) {
        all = ['Movies', 'TV Shows', 'Music Videos', 'Interviews', 'Commercials', 'Podcasts'];
    } else if (appName.includes('marinadelreyfilmfestival')) {
        all = ['Movies', 'TV Shows', 'Commercials'];
    } else if (appName.includes('usaboxing')) {
        all = ['TV Shows', 'Interviews', 'Podcasts', 'Commercials'];
    } else if (appName.includes('westlosangelescollege')) {
        all = ['TV Shows', 'Interviews', 'Podcasts', 'Commercials'];
    } else {
        all = ['Movies', 'Shows'];
    }

    return all.filter(m => !EXCLUDE.has(m));
}

export default function HeirloomAISearchPage() {
    const [query, setQuery]               = useState('');
    const [categories]                    = useState(() => getCategories());
    const [selectedCats, setCats]         = useState([]);
    const [selectedModels, setModels]     = useState([]);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [modelOpen, setModelOpen]       = useState(false);
    const [isListening, setIsListening]   = useState(false);
    const [micSupported, setMicSupported] = useState(true);
    const [keyboardOffset, setKeyboardOffset] = useState(0);
    const [isMobile, setIsMobile]         = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

    const categoryRef    = useRef(null);
    const modelRef       = useRef(null);
    const inputRef       = useRef(null);
    const recognitionRef = useRef(null);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false);
            if (modelRef.current && !modelRef.current.contains(e.target)) setModelOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Mobile breakpoint detection
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    // Track soft keyboard via visualViewport so the pill floats above it
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        const handler = () => {
            const offset = window.innerHeight - vv.height - vv.offsetTop;
            setKeyboardOffset(Math.max(0, Math.round(offset)));
        };
        vv.addEventListener('resize', handler);
        vv.addEventListener('scroll', handler);
        return () => {
            vv.removeEventListener('resize', handler);
            vv.removeEventListener('scroll', handler);
        };
    }, []);

    // Init Web Speech API
    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setMicSupported(false); return; }

        const rec = new SR();
        rec.continuous     = false;
        rec.interimResults = true;
        rec.lang           = 'en-US';

        rec.onresult = (event) => {
            const transcript = Array.from(event.results)
                .map(r => r[0].transcript)
                .join('');
            setQuery(transcript);
        };
        rec.onend   = () => setIsListening(false);
        rec.onerror = () => setIsListening(false);

        recognitionRef.current = rec;
        return () => recognitionRef.current && recognitionRef.current.abort();
    }, []);

    const toggleMic = () => {
        if (!recognitionRef.current) return;
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            setQuery('');
            setCategoryOpen(false);
            setModelOpen(false);
            recognitionRef.current.start();
            setIsListening(true);
            setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
        }
    };

    const toggleCat   = (c) => setCats(p  => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);
    const toggleModel = (m) => setModels(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);

    const handleSearch = () => {
        if (!query.trim() && !selectedCats.length && !selectedModels.length) return;
        console.log('AI Search', { query, selectedCats, selectedModels });
    };

    const hasSelections = selectedCats.length > 0 || selectedModels.length > 0;

    /* ─── pill styles ──────────────────────────────────────────── */
    const pillStyle = {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        maxWidth: '760px',
        minHeight: '60px',
        background: '#18182a',
        border: '1px solid rgba(255,255,255,0.11)',
        borderRadius: '56px',
        padding: '6px 6px 6px 4px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
        position: 'relative',
        gap: '0',
    };

    const sepStyle = {
        flexShrink: 0,
        width: '1px',
        height: '26px',
        background: 'rgba(255,255,255,0.1)',
        alignSelf: 'center',
    };

    /* ─── pill area — fixed bottom on mobile, inline on desktop ── */
    const pillAreaStyle = isMobile ? {
        position: 'fixed',
        bottom: keyboardOffset + 8,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '0 12px 8px',
        background: 'linear-gradient(to top, #0f0f0f 65%, transparent)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    } : {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    };

    const pillJSX = (
        <div style={pillStyle}>

            {/* Category dropdown */}
            <div className="ai-dd-wrap" ref={categoryRef}>
                <button
                    type="button"
                    className={`ai-dd-btn ai-dd-btn--icon${selectedCats.length ? ' ai-dd-btn--on' : ''}`}
                    onClick={() => { setCategoryOpen(o => !o); setModelOpen(false); }}
                    title={selectedCats.length ? `Category (${selectedCats.length})` : 'Category'}
                    aria-label="Category"
                >
                    {selectedCats.length
                        ? <span className="ai-cat-count">{selectedCats.length}</span>
                        : <span className="material-symbols-outlined ai-plus-icon">add</span>
                    }
                </button>

                {categoryOpen && (
                    <div className="ai-menu ai-menu--left">
                        <span className="ai-menu-label">Content Category</span>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                type="button"
                                className={`ai-menu-row${selectedCats.includes(cat) ? ' ai-menu-row--on' : ''}`}
                                onClick={() => toggleCat(cat)}
                            >
                                <span className="ai-chk">
                                    {selectedCats.includes(cat) && <span className="material-symbols-outlined">check</span>}
                                </span>
                                {cat}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div style={sepStyle} />

            {/* Input + tags */}
            <div
                className="ai-input-wrap"
                onClick={() => inputRef.current && inputRef.current.focus()}
            >
                {selectedCats.map(c => (
                    <span key={c} className="ai-tag ai-tag--cat">
                        {c}
                        <button type="button" className="ai-tag-x" onClick={(e) => { e.stopPropagation(); toggleCat(c); }}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </span>
                ))}
                {selectedModels.map(m => (
                    <span key={m} className="ai-tag ai-tag--model">
                        {m}
                        <button type="button" className="ai-tag-x" onClick={(e) => { e.stopPropagation(); toggleModel(m); }}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    className="ai-text-input"
                    placeholder={isListening ? 'Listening...' : hasSelections ? '' : 'Ask AI...'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
            </div>

            {/* <div style={sepStyle} /> */}

            {/* AI Model dropdown */}
            <div className="ai-dd-wrap" ref={modelRef}>
                <button
                    type="button"
                    className={`ai-dd-btn${selectedModels.length ? ' ai-dd-btn--on' : ''}`}
                    onClick={() => { setModelOpen(o => !o); setCategoryOpen(false); }}
                >
                    {selectedModels.length ? `AI (${selectedModels.length})` : (isMobile ? 'AI' : 'AI Model')}
                    <span className="material-symbols-outlined ai-chev">
                        {modelOpen ? 'expand_less' : 'expand_more'}
                    </span>
                </button>

                {modelOpen && (
                    <div className="ai-menu ai-menu--right">
                        <span className="ai-menu-label">Platform &amp; Model</span>
                        {AI_MODELS.map(({ platform, models }) => (
                            <div key={platform}>
                                <p className="ai-menu-platform">{platform}</p>
                                {models.map(m => (
                                    <button
                                        key={m}
                                        type="button"
                                        className={`ai-menu-row${selectedModels.includes(m) ? ' ai-menu-row--on' : ''}`}
                                        onClick={() => toggleModel(m)}
                                    >
                                        <span className="ai-chk">
                                            {selectedModels.includes(m) && <span className="material-symbols-outlined">check</span>}
                                        </span>
                                        {m}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Mic */}
            {micSupported && (
                <button
                    type="button"
                    className={`ai-mic-btn${isListening ? ' ai-mic-btn--active' : ''}`}
                    onClick={toggleMic}
                    aria-label={isListening ? 'Stop listening' : 'Search by voice'}
                    title={isListening ? 'Stop' : 'Search by voice'}
                >
                    <span className="material-symbols-outlined">mic</span>
                </button>
            )}

            {/* Submit — only when text is present */}
            {query.trim() && (
                <button
                    type="button"
                    className="ai-submit-btn ai-submit-btn--active"
                    onClick={handleSearch}
                    aria-label="Submit search"
                    title="Submit"
                >
                    <span className="material-symbols-outlined">arrow_circle_up</span>
                </button>
            )}

        </div>
    );

    return (
        <div className="ai-search-page">
            <Header />

            <main className="ai-search-main" style={{
                minHeight: 'calc(-180px + 100vh)',
                paddingBottom: isMobile ? '120px' : '80px',
            }}>
                <h1 className="ai-heading">Where should we start?</h1>

                {/* Desktop: pill inline with the heading */}
                {!isMobile && (
                    <div style={pillAreaStyle}>
                        {pillJSX}
                        <p className="ai-hint">
                            {isListening
                                ? "Speak now — we're listening..."
                                : 'Pick a category and AI model, then type or speak your query.'}
                        </p>
                    </div>
                )}
            </main>

            {/* Mobile: pill fixed at bottom, floats above keyboard */}
            {isMobile && (
                <div style={pillAreaStyle}>
                    {pillJSX}
                    <p className="ai-hint" style={{ fontSize: 12, marginTop: 6 }}>
                        {isListening ? "Listening..." : 'Type or speak your query.'}
                    </p>
                </div>
            )}

            {!isMobile && <Footer />}
        </div>
    );
}
